import * as Sentry from '@sentry/react';
import { hydrate, MutationCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { ApiProvider } from '@hero/context';
import {
  ChangeLanguageConfigProvider,
  WebPostHogProvider,
  setupWebQueryFocusManager,
} from '@hero/web-hooks';
import { LiveAppSubGate } from '@hero/web-components';
import { shouldPersistQuery, PERSIST_MAX_AGE, PERSIST_CACHE_KEY } from '@hero/api-hooks';
import { setEventErrorReporter } from '@hero/core';
import App from './app/app';
import { ReferralCapture } from './components/features/Referral/ReferralCapture';
import { carrierAPI } from './services/api';
import { APP_CONFIG } from './config';
import i18n from './utils/i18n';
import './index.css';
import './utils/sentry';

if (APP_CONFIG.googleAnalytics.shouldActivate) {
  ReactGA.initialize(import.meta.env.VITE_ANALYTICS_MEASUREMENT_ID);
}

setEventErrorReporter((err, { event }) => {
  Sentry.captureException(err, {
    tags: { analytics: 'capture' },
    extra: { event },
  });
});

/**
 * Global mutation error fallback — when a mutation rejects and the
 * call site didn't supply its own `onError`, surface the SDK error
 * message via the dashboard `<Toaster>`. Without this, drawers that
 * `await update.mutateAsync(...)` inside a try/catch and just
 * `console.error` (the typical pattern, e.g. the financing carrier
 * legal / banking drawers) would silently fail from the user's POV.
 *
 * We deliberately gate on `mutation.options.onError` rather than a
 * meta flag: the existing custom-toast call sites in the app
 * (`useOrderAPIs`, `AppsGrid`, auth hooks) all pass `onError`, so
 * they'll continue to render their own (often more contextual)
 * toast without us double-toasting.
 *
 * `meta.silentError === true` is an explicit opt-out for call sites
 * that genuinely want to swallow an error (rare).
 */
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      if (mutation.meta?.silentError === true) return;
      const message =
        (error as { message?: string } | null | undefined)?.message ||
        i18n.t('c-main:errors.generic');
      toast.error(message);
    },
  }),
});

const persister = createAsyncStoragePersister({ storage: window.localStorage });

// Pre-hydrate from localStorage synchronously so the first paint already has
// cached data (user profile, etc.). PersistQueryClientProvider handles ongoing saves.
try {
  const raw = window.localStorage.getItem(PERSIST_CACHE_KEY);
  if (raw) {
    const cached = JSON.parse(raw) as { timestamp: number; clientState: unknown };
    if (cached.timestamp + PERSIST_MAX_AGE > Date.now()) {
      hydrate(queryClient, cached.clientState);
    }
  }
} catch {
  /* corrupted cache — ignore */
}

setupWebQueryFocusManager();

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}>
      <ApiProvider api={carrierAPI}>
        <ReferralCapture>
          {/*
           * `reloadOnChange` was previously set here to force a full
           * `window.location.reload()` after every language change.
           * That is unnecessary — `applyLanguageLocally` inside
           * `useChangeLanguageManager` already flips
           * `document.documentElement.dir`, the `font-almarai` /
           * `font-inter` class on `<body>`, and calls
           * `i18n.changeLanguage(...)`. Combined with the Tailwind
           * logical utilities the shared components already use
           * (`ms-*`, `me-*`, `start-*`, `end-*`, `rtl:*`), the UI
           * reflows live. Dropping the reload also matches the
           * mobile behavior — a language toggle should never feel
           * like it's tearing down the session.
           */}
          <ChangeLanguageConfigProvider>
            <WebPostHogProvider apiKey={import.meta.env.VITE_POSTHOG_PROJECT_TOKEN} app="dashboard">
              <LiveAppSubGate subFlag="sub_dashboard">
                <Sentry.ErrorBoundary>
                  <App />
                </Sentry.ErrorBoundary>
              </LiveAppSubGate>
            </WebPostHogProvider>
          </ChangeLanguageConfigProvider>
        </ReferralCapture>
      </ApiProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
